import { LoadingScreen } from '@avanta/shared/components/LoadingScreen';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';
import { Survey } from 'components/Survey';
import { SurveyChart } from 'components/SurveyChart';
import { Breadcrumb } from 'components/ui/Breadcrumb';
import { ROUTES } from 'constants/routes';
import { SURVEY_TYPES } from 'constants/surveys';
import { useGetSurveys } from 'hooks/useGetSurveys';
import { TestCard } from './components/TestCard';
import { DEFAULT_SURVEY_TAG } from './components/TestCard/utils';

export const TestWellbeing = (): React.ReactElement => {
  const { t } = useTranslation(LOCIZE_NS.WELL_BEING);
  const { surveyId } = useParams<{ surveyId?: string }>();
  const navigate = useNavigate();
  const { data: surveys, isLoading } = useGetSurveys(SURVEY_TYPES.TEST);

  const surveyProps = {
    surveyType: SURVEY_TYPES.TEST,
    title: t('WellBeing.Test.heading'),
    allowRepeat: true,
    endCardTitle: t('WellBeing.Test.End.heading'),
    endCardSubtitle: t('WellBeing.Test.End.body'),
    saveButton: 'WellBeing.Test.cta',
    repeatButton: t('WellBeing.Test.cancel'),
  };

  const selectedSurvey = surveyId ? surveys?.find(s => s.id === surveyId) : undefined;

  const seen = new Set<string>();
  const uniqueSurveysByTag = surveys?.filter(survey => {
    const tag = survey.tag ?? DEFAULT_SURVEY_TAG;
    if (seen.has(tag)) return false;
    seen.add(tag);
    return true;
  });

  const handleStartTest = (id: string): void => {
    navigate(`${ROUTES.WELL_BEING_TEST}/${id}`);
  };

  const handleClose = (): void => {
    navigate(ROUTES.WELL_BEING_TEST);
  };

  const handleCompleted = (): void => {
    navigate(ROUTES.WELL_BEING_TEST);
  };

  if (surveyId) {
    if (isLoading || !selectedSurvey) {
      return <LoadingScreen />;
    }

    return (
      <Survey
        {...surveyProps}
        onClose={handleClose}
        onCompleted={handleCompleted}
        endCardChildren={
          <SurveyChart
            surveyType={SURVEY_TYPES.TEST}
            surveyId={selectedSurvey.id}
            title={t('DailyTracking.End.evolution.heading')}
            showFilters={true}
          />
        }
      />
    );
  }

  if (isLoading) return <LoadingScreen />;

  return (
    <div className="flex flex-col gap-8">
      <Breadcrumb
        links={[
          { href: ROUTES.WELL_BEING, label: t('Nav.wellBeing', { ns: LOCIZE_NS.COMMON }) },
          { href: ROUTES.WELL_BEING_TEST, label: t('WellBeing.Categories.Test.heading') },
        ]}
      />
      <div className="flex flex-col gap-6 w-full">
        {uniqueSurveysByTag?.map(survey => (
          <TestCard key={survey.id} survey={survey} onStartTest={handleStartTest} />
        ))}
      </div>
    </div>
  );
};
